/**
 * Match Detail Mobile Component
 * Mobile layout with live interface and betting options
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useBetting } from '@/Context/BettingContext';
import { useBookmakerFancy } from '@/hooks/useBookmakerFancy';
import SportsBetSlip from '../SportsBetSlip/SportsBetSlip';
import MatchOdds from './MatchOdds';
import styles from './MatchDetailMobile.module.css';

const MatchDetailMobile = ({ matchData }) => {
  const navigate = useNavigate();
  const { selectedBets, setSelectedBets } = useBetting();
  const [showStream, setShowStream] = useState(false);
  const [showBetSlip, setShowBetSlip] = useState(false);
  const prevBetsLengthRef = useRef(0);

  // Open betslip on mobile when a bet is added via odds click
  useEffect(() => {
    // Only open if bet count increased from 0 to 1+ (new bet added)
    if (selectedBets.length > prevBetsLengthRef.current && prevBetsLengthRef.current === 0) {
      setShowBetSlip(true);
    }
    prevBetsLengthRef.current = selectedBets.length;
  }, [selectedBets.length]);

  // Clear betslip data when closing the modal
  const handleCloseBetslip = () => {
    setShowBetSlip(false);
    // Clear all selected bets when closing
    setSelectedBets([]);
    prevBetsLengthRef.current = 0; // Reset ref when clearing
  };

  // Use React Query hook for bookmaker/fancy data
  const { data: bookmakerFancyData } = useBookmakerFancy(matchData?.eventid);
  const bookmakerData = bookmakerFancyData?.bookMaker || [];
  const fancyData = bookmakerFancyData?.fancy || [];

  const toggleStream = () => {
    setShowStream(!showStream);
  };

  return (
    <div className={styles.matchDetailMobile}>
      {/* Live Interface Header */}
      <div className={styles.liveInterface}>
        <div className={styles.liveHeader}>
          <div className={styles.headerLeft}>
            <button className={styles.backButton} onClick={() => navigate(-1)}>
              <ArrowLeft size={20} />
            </button>
            <h2 className={styles.liveTitle}>Live Score</h2>
          </div>
          <div className={styles.headerRight}>
            <button className={styles.streamToggle} onClick={toggleStream}>
              {showStream ? 'Stop Stream' : 'Play Stream'}
            </button>
          </div>
        </div>

        {/* Live Stream */}
        {showStream && matchData?.eventid && (
          <div className={styles.streamContainer}>
            <iframe
              src={`https://live-stream.softgamingapi.com/?eventid=${matchData.eventid}`}
              title="Live Stream"
              className={styles.streamFrame}
              allowFullScreen
            />
          </div>
        )}

        {/* Score Card */}
        {matchData?.eventid && (
          <div className={styles.scoreContainer}>
            <iframe
              src={`https://betfair-score.softgamingapi.com/sport-radar-ui?event_id=${matchData.eventid}`}
              title="Live Score"
              className={styles.scoreFrame}
            />
          </div>
        )}
      </div>

      {/* Match Title */}
      {matchData && (
        <div className={styles.matchTitle}>
          {matchData.team1} v {matchData.team2}
        </div>
      )}

      {/* Betting Sections */}
      <div className={styles.bettingSections}>
        {/* In-Play Betting */}
        <div className={styles.bettingSection}>
          <h3 className={styles.sectionTitle}>Match Odds</h3>
          <MatchOdds matchData={matchData} />
        </div>

        {/* Fancy Betting */}
        {fancyData.length > 0 && (
          <div className={styles.bettingSection}>
            <h3 className={styles.sectionTitle}>Fancy Markets</h3>
            {/* Add fancy betting UI here */}
          </div>
        )}
      </div>

      {/* Bet Slip */}
      <SportsBetSlip isOpen={showBetSlip} onClose={handleCloseBetslip} />
    </div>
  );
};

export default MatchDetailMobile;
